import type { ReactNode } from 'react';
import { GraduationCap, ShieldCheck, UsersRound } from 'lucide-react';
import { LogoMark } from '@/components/shell/Logo';

const points = [
  { icon: GraduationCap, title: 'Learning that is tracked', text: 'Attendance, tests, ranks, homework and downloadable study material for every batch.' },
  { icon: UsersRound, title: 'Parents stay in the loop', text: 'A parent app with progress, attendance, fees and a direct line to the mentor.' },
  { icon: ShieldCheck, title: 'Built for trust', text: 'Role-based access and a full audit trail for every change.' },
];

/** Split-screen frame for sign-in pages: brand story on the left, the form on the right. */
export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <aside className="relative hidden overflow-hidden bg-brand-900 p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="absolute -right-24 -top-24 size-96 rounded-full bg-brand-700/40 blur-3xl" aria-hidden />
        <div className="absolute -bottom-32 -left-16 size-96 rounded-full bg-brand-500/20 blur-3xl" aria-hidden />
        <div className="relative flex items-center gap-3">
          <LogoMark fill="#FFFFFF" glyph="#134E4A" className="size-10" />
          <span className="font-display text-2xl font-extrabold tracking-tight">TCM</span>
        </div>
        <div className="relative max-w-md">
          <h2 className="font-display text-4xl font-bold leading-tight tracking-tight text-white">Every batch, test and parent, in one place.</h2>
          <ul className="mt-10 space-y-6">
            {points.map(({ icon: Icon, title, text }) => (
              <li key={title} className="flex gap-4">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white/10">
                  <Icon className="size-5 text-brand-200" aria-hidden />
                </span>
                <div>
                  <p className="font-semibold text-white">{title}</p>
                  <p className="mt-0.5 text-sm text-brand-200/80">{text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-brand-300/70">© {new Date().getFullYear()} TCM. Software for coaching and tuition centers.</p>
      </aside>

      <main className="flex flex-col justify-center px-5 py-10 sm:px-10">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <LogoMark />
            <span className="font-display text-xl font-extrabold tracking-tight text-slate-900">TCM</span>
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
